import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calendar, 
  FileText, 
  Users, 
  Car, 
  CheckCircle, 
  Clock, 
  BarChart3, 
  Shield,
  Smartphone,
  ArrowRight,
  ChevronRight,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  X,
  Loader2,
  Download,
  Eye,
  TrendingUp,
  Layers,
  UserCheck,
  ClipboardCheck,
  FileSpreadsheet,
  Truck,
  CircleDot,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOSOFT_TEAL = '#6BA3B7';
const DOSOFT_GRAY = '#4A4A4A';

const WHATSAPP_NUMBER = '56990934576';
const WHATSAPP_MESSAGE = 'Hola, me interesa conocer más sobre el sistema de gestión de traslados DoSoft.';

/* ======== Animated Screencast Component ======== */
const BookingScreencast = () => {
  const [step, setStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => setStep(s => (s + 1) % 5), 3000);
    return () => clearInterval(timer);
  }, [isVisible]);

  const steps = [
    { label: 'Solicitar Servicio', sublabel: 'El cliente completa el formulario' },
    { label: 'Asignar Recursos', sublabel: 'Vehículo y conductor asignados' },
    { label: 'Confirmar Vale', sublabel: 'El administrador confirma' },
    { label: 'Marcar Realizado', sublabel: 'Servicio completado con tarifa final' },
    { label: 'Generar Reporte', sublabel: 'Exportar a Excel o PDF' },
  ];

  return (
    <div ref={ref} className="relative w-full max-w-2xl mx-auto">
      {/* Mock browser frame */}
      <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-200">
        {/* Browser top bar */}
        <div className="bg-gray-100 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
          </div>
          <div className="flex-1 mx-4 bg-white rounded-md px-3 py-1 text-xs text-gray-400 border border-gray-200">
            velify.dosoft.cl/admin/solicitar-viaje
          </div>
        </div>

        {/* App content */}
        <div className="bg-white flex" style={{ height: 340 }}>
          {/* Sidebar */}
          <div className="w-36 bg-gray-50 border-r border-gray-100 p-3 flex-shrink-0">
            <div className="w-20 h-6 bg-gray-200 rounded mb-4"></div>
            {['Calendario', 'Solicitar', 'Clientes', 'Reportes', 'Vehículos'].map((item, i) => (
              <div
                key={i}
                className={`text-xs py-2 px-2 rounded mb-1 transition-all duration-500 ${
                  (step === 0 && i === 1) || (step === 1 && i === 0) || (step === 2 && i === 0) || (step === 3 && i === 0) || (step === 4 && i === 3)
                    ? 'text-white font-medium'
                    : 'text-gray-500'
                }`}
                style={{
                  backgroundColor: (step === 0 && i === 1) || (step === 1 && i === 0) || (step === 2 && i === 0) || (step === 3 && i === 0) || (step === 4 && i === 3) ? DOSOFT_TEAL : 'transparent'
                }}
              >
                {item}
              </div>
            ))}
          </div>

          {/* Main area */}
          <div className="flex-1 p-4 overflow-hidden relative">
            {/* Step 0: Form */}
            <div className={`absolute inset-0 p-4 transition-all duration-700 ${step === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="text-sm font-semibold text-gray-700 mb-3">Solicitar Nuevo Viaje</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { l: 'Pasajero', v: 'Pedro Martínez' },
                  { l: 'Teléfono', v: '+569 8765 4321' },
                  { l: 'Fecha', v: '25/04/2026' },
                  { l: 'Hora', v: '08:30' },
                  { l: 'Origen', v: 'Terminal Norte' },
                  { l: 'Destino', v: 'Faena Minera Sur' },
                ].map((f, i) => (
                  <div key={i} className={`transition-all duration-500 ${step === 0 ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: `${i * 150}ms` }}>
                    <div className="text-[10px] text-gray-400 mb-0.5">{f.l}</div>
                    <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-700">{f.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <div className="text-xs text-white px-4 py-2 rounded-md font-medium" style={{ backgroundColor: DOSOFT_TEAL }}>
                  Enviar Solicitud
                </div>
              </div>
            </div>

            {/* Step 1: Calendar assign */}
            <div className={`absolute inset-0 p-4 transition-all duration-700 ${step === 1 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="text-sm font-semibold text-gray-700 mb-3">Calendario - Abril 2026</div>
              <div className="grid grid-cols-7 gap-1 text-[10px] text-center mb-2 text-gray-400">
                {['Lu','Ma','Mi','Ju','Vi','Sa','Do'].map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1 text-[10px] text-center">
                {Array.from({length: 30}, (_, i) => i + 1).map(d => (
                  <div key={d} className={`py-1.5 rounded transition-all duration-300 ${
                    d === 25 ? 'text-white font-bold' : d === 18 || d === 20 ? 'bg-green-100 text-green-700' : d === 22 ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'
                  }`} style={{ backgroundColor: d === 25 ? DOSOFT_TEAL : undefined }}>
                    {d}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-500"></div><span className="text-[10px] text-gray-500">Confirmado</span>
                <div className="w-2 h-2 rounded-full bg-green-500"></div><span className="text-[10px] text-gray-500">Realizado</span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: DOSOFT_TEAL }}></div><span className="text-[10px] text-gray-500">Nuevo</span>
              </div>
            </div>

            {/* Step 2: Confirm modal */}
            <div className={`absolute inset-0 p-4 transition-all duration-700 ${step === 2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="text-sm font-semibold text-gray-700 mb-3">Confirmar Servicio</div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-gray-500">Folio</span><span className="font-bold" style={{ color: DOSOFT_TEAL }}>#0025</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Pasajero</span><span className="font-medium text-gray-700">Pedro Martínez</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Ruta</span><span className="text-gray-700">Terminal → Faena Sur</span></div>
                <div className="border-t border-gray-200 my-1"></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Vehículo</span><span className="text-gray-700">BBCC-12 (Toyota Hilux)</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Conductor</span><span className="text-gray-700">Héctor Valenzuela</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Tarifa</span><span className="font-bold text-gray-800">$185.000</span></div>
              </div>
              <div className="mt-3 flex gap-2 justify-end">
                <div className="text-xs text-gray-500 px-3 py-2 rounded border border-gray-200">Cancelar</div>
                <div className="text-xs text-white px-4 py-2 rounded font-medium bg-indigo-600">Confirmar</div>
              </div>
            </div>

            {/* Step 3: Mark as done */}
            <div className={`absolute inset-0 p-4 transition-all duration-700 ${step === 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="text-sm font-semibold text-gray-700 mb-3">Marcar como Realizado</div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">Servicio Folio #0025</span>
                </div>
                <div className="space-y-2">
                  <div><span className="text-[10px] text-gray-500 block">Tarifa Final</span><div className="bg-white border rounded px-2 py-1.5 text-sm font-bold text-gray-800">$185.000</div></div>
                  <div><span className="text-[10px] text-gray-500 block">Tiempo de Espera</span><div className="bg-white border rounded px-2 py-1.5 text-sm text-gray-700">30 min</div></div>
                </div>
                <div className="text-xs text-white px-4 py-2 rounded font-medium bg-green-600 text-center">Confirmar Realizado</div>
              </div>
            </div>

            {/* Step 4: Report */}
            <div className={`absolute inset-0 p-4 transition-all duration-700 ${step === 4 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="text-sm font-semibold text-gray-700 mb-2">Reportes de Cuadratura</div>
              <div className="flex gap-1 mb-2">
                <div className="text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-600">Mar 2026</div>
                <div className="text-[10px] px-2 py-1 rounded text-white" style={{ backgroundColor: DOSOFT_TEAL }}>Excel</div>
                <div className="text-[10px] px-2 py-1 rounded text-white bg-red-500">PDF</div>
                <div className="text-[10px] px-2 py-1 rounded text-white bg-indigo-500">Vales</div>
              </div>
              <div className="border rounded overflow-hidden text-[9px]">
                <div className="grid grid-cols-6 bg-gray-100 py-1 px-1 font-medium text-gray-600">
                  <div>Folio</div><div>Fecha</div><div>Pasajero</div><div>Ruta</div><div>T. Espera</div><div>Tarifa</div>
                </div>
                {[
                  { f:'#23', d:'20/03', p:'P. Martínez', r:'TN→FS', t:'15 min', ta:'$120.000' },
                  { f:'#24', d:'22/03', p:'L. Herrera', r:'FS→TN', t:'-', ta:'$95.000' },
                  { f:'#25', d:'25/03', p:'R. Vargas', r:'TN→FS', t:'30 min', ta:'$185.000' },
                  { f:'#26', d:'28/03', p:'M. Torres', r:'Base→FS', t:'-', ta:'$75.000' },
                ].map((r, i) => (
                  <div key={i} className={`grid grid-cols-6 py-1 px-1 ${i % 2 ? 'bg-gray-50' : ''} transition-all duration-500`} style={{ transitionDelay: `${i * 200}ms`, opacity: step === 4 ? 1 : 0 }}>
                    <div className="font-bold" style={{ color: DOSOFT_TEAL }}>{r.f}</div><div>{r.d}</div><div>{r.p}</div><div>{r.r}</div><div>{r.t}</div><div className="font-bold">{r.ta}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-right text-xs font-bold text-gray-800">Total: $475.000</div>
            </div>
          </div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-3 mt-6">
        {steps.map((s, i) => (
          <button key={i} onClick={() => setStep(i)} className="text-center group cursor-pointer">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mx-auto mb-1 transition-all duration-300 ${
              step === i ? 'text-white scale-110' : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
            }`} style={{ backgroundColor: step === i ? DOSOFT_TEAL : undefined }}>
              {i + 1}
            </div>
            <div className={`text-[10px] font-medium transition-colors duration-300 ${step === i ? '' : 'text-gray-400'}`} style={{ color: step === i ? DOSOFT_TEAL : undefined }}>
              {s.label}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ======== Visual Mockup Components ======== */
const CalendarioMockup = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-700">Abril 2026</span>
      <div className="flex gap-1">
        <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">&lt;</div>
        <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">&gt;</div>
      </div>
    </div>
    <div className="p-3">
      <div className="grid grid-cols-7 gap-1 text-[10px] text-center mb-2 font-medium text-gray-400">
        {['Lu','Ma','Mi','Ju','Vi','Sa','Do'].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-center">
        {[null,null,null,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,null,null].map((d, i) => {
          if (!d) return <div key={i}></div>;
          const hasEvent = [7,8,14,15,18,21,22,25,28].includes(d);
          const colors = { 7:'#10B981', 8:'#10B981', 14:'#6366F1', 15:'#EF4444', 18:'#6366F1', 21:DOSOFT_TEAL, 22:DOSOFT_TEAL, 25:'#10B981', 28:'#F59E0B' };
          return (
            <div key={i} className="py-1 relative">
              <span className="text-gray-600">{d}</span>
              {hasEvent && <div className="w-4 h-1 rounded-full mx-auto mt-0.5" style={{ backgroundColor: colors[d] }}></div>}
            </div>
          );
        })}
      </div>
    </div>
    <div className="px-3 pb-3 space-y-1.5">
      {[
        { t:'06:00 - P. Martínez → Faena Norte', c:'#6366F1', s:'Confirmado' },
        { t:'08:30 - L. Herrera → Terminal Aéreo', c:'#10B981', s:'Realizado' },
        { t:'14:00 - R. Vargas → Planta Sur', c:DOSOFT_TEAL, s:'Agendado' },
      ].map((ev, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-md transition-all hover:bg-gray-50" style={{ borderLeft: `3px solid ${ev.c}` }}>
          <div className="flex-1 text-[10px] text-gray-700 truncate">{ev.t}</div>
          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: ev.c }}>{ev.s}</span>
        </div>
      ))}
    </div>
  </div>
);

const ReportesMockup = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-700">Cuadratura - Abril 2026</span>
      <div className="flex gap-1">
        <div className="text-[9px] px-2 py-1 rounded-md text-white font-medium" style={{ backgroundColor: DOSOFT_TEAL }}>Excel</div>
        <div className="text-[9px] px-2 py-1 rounded-md text-white font-medium bg-red-500">PDF</div>
        <div className="text-[9px] px-2 py-1 rounded-md text-white font-medium bg-indigo-500">Vales</div>
      </div>
    </div>
    {/* KPIs */}
    <div className="grid grid-cols-3 gap-2 p-3">
      {[
        { label:'Servicios', val:'56', sub:'programados', c: DOSOFT_TEAL },
        { label:'Realizados', val:'41', sub:'facturables', c:'#10B981' },
        { label:'Monto Total', val:'$6.2M', sub:'por cobrar', c:'#6366F1' },
      ].map((k, i) => (
        <div key={i} className="text-center p-2 rounded-lg" style={{ backgroundColor: `${k.c}08` }}>
          <div className="text-lg font-bold" style={{ color: k.c }}>{k.val}</div>
          <div className="text-[9px] font-medium text-gray-600">{k.label}</div>
          <div className="text-[8px] text-gray-400">{k.sub}</div>
        </div>
      ))}
    </div>
    {/* Mini table */}
    <div className="px-3 pb-3">
      <div className="border rounded-lg overflow-hidden text-[9px]">
        <div className="grid grid-cols-5 bg-gray-50 py-1.5 px-2 font-semibold text-gray-500 border-b">
          <div>Folio</div><div>Fecha</div><div>Cliente</div><div>T. Espera</div><div className="text-right">Tarifa</div>
        </div>
        {[
          { f:'#041', d:'02/04', cl:'Empresa ABC', t:'20 min', ta:'$145.000' },
          { f:'#042', d:'05/04', cl:'Minera del Sol', t:'-', ta:'$210.000' },
          { f:'#043', d:'08/04', cl:'Logística Andes', t:'45 min', ta:'$185.000' },
          { f:'#044', d:'12/04', cl:'Constructora Río', t:'-', ta:'$95.000' },
        ].map((r, i) => (
          <div key={i} className={`grid grid-cols-5 py-1.5 px-2 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
            <div className="font-bold" style={{ color: DOSOFT_TEAL }}>{r.f}</div>
            <div className="text-gray-600">{r.d}</div>
            <div className="text-gray-700 truncate">{r.cl}</div>
            <div className={r.t !== '-' ? 'text-amber-600 font-medium' : 'text-gray-300'}>{r.t}</div>
            <div className="text-right font-semibold text-gray-800">{r.ta}</div>
          </div>
        ))}
        <div className="grid grid-cols-5 py-1.5 px-2 border-t bg-gray-50 font-bold">
          <div className="col-span-4 text-right text-gray-600">Total Realizado:</div>
          <div className="text-right" style={{ color: DOSOFT_TEAL }}>$635.000</div>
        </div>
      </div>
    </div>
  </div>
);

const FlotaMockup = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-700">Flota y Conductores</span>
      <div className="text-[9px] px-2 py-1 rounded-md text-white font-medium" style={{ backgroundColor: DOSOFT_TEAL }}>+ Agregar</div>
    </div>
    <div className="p-3 space-y-2">
      {[
        { pat:'BBCC-12', marca:'Toyota Hilux SRV 2024', cap:'4 pas.', conductor:'Héctor Valenzuela', rut:'Lic. A2', status:'Disponible', sc:'#10B981' },
        { pat:'DDEE-34', marca:'Hyundai H1 Premium 2023', cap:'9 pas.', conductor:'Francisco Reyes', rut:'Lic. A3', status:'En ruta', sc:'#F59E0B' },
        { pat:'FFGG-56', marca:'Mercedes Sprinter 2025', cap:'15 pas.', conductor:'Rodrigo Espinoza', rut:'Lic. A4', status:'Disponible', sc:'#10B981' },
      ].map((v, i) => (
        <div key={i} className="rounded-lg border border-gray-100 p-3 hover:shadow-md transition-all hover:border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${DOSOFT_TEAL}12` }}>
                <Car className="w-4 h-4" style={{ color: DOSOFT_TEAL }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-gray-800 tracking-wide">{v.pat}</span>
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: v.sc }}>{v.status}</span>
                </div>
                <div className="text-[10px] text-gray-500">{v.marca}</div>
              </div>
            </div>
            <div className="text-[9px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{v.cap}</div>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-gray-500 pl-11">
            <div className="flex items-center gap-1"><User className="w-3 h-3" />{v.conductor}</div>
            <div className="text-gray-300">|</div>
            <div className="text-gray-400">{v.rut}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ClientesMockup = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-700">Clientes y Usuarios</span>
      <div className="flex gap-2">
        <span className="text-[8px] px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: '#6366F1' }}>Administrador</span>
        <span className="text-[8px] px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: '#10B981' }}>Cliente</span>
      </div>
    </div>
    <div className="p-3 space-y-1.5">
      {[
        { name:'Administrador Demo', email:'admin@miempresa.cl', role:'Admin', rc:'#6366F1', empresa:'Mi Empresa SpA', rut:'77.000.000-0' },
        { name:'Gonzalo Reyes', email:'greyes@clienteabc.cl', role:'Cliente', rc:'#10B981', empresa:'Empresa ABC', rut:'76.100.200-3' },
        { name:'Carolina Soto', email:'csoto@mineradelsol.cl', role:'Cliente', rc:'#10B981', empresa:'Minera del Sol', rut:'76.200.300-5' },
        { name:'Andrés Muñoz', email:'amunoz@logandes.cl', role:'Cliente', rc:'#10B981', empresa:'Logística Andes', rut:'76.300.400-7' },
      ].map((u, i) => (
        <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-all border border-transparent hover:border-gray-100">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: u.rc }}>
            {u.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">{u.name}</span>
              <span className="text-[7px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: u.rc }}>{u.role}</span>
            </div>
            <div className="text-[10px] text-gray-400">{u.email}</div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] font-medium text-gray-600">{u.empresa}</div>
            <div className="text-[9px] text-gray-400">{u.rut}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ValePDFMockup = () => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    {/* PDF-like header */}
    <div className="p-4" style={{ borderBottom: `3px solid ${DOSOFT_TEAL}` }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
            <Truck className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-700">Tu Empresa SpA</div>
            <div className="text-[9px] text-gray-400">Transporte de Personal</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold" style={{ color: DOSOFT_TEAL }}>Vale Digital</div>
          <div className="text-lg font-black" style={{ color: DOSOFT_TEAL }}>Nº 043</div>
        </div>
      </div>
    </div>
    {/* Content grid */}
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-4 text-[10px]">
      {[
        { l:'CLIENTE', v:'Minera del Sol S.A.' },
        { l:'PASAJERO', v:'Carolina Soto V.' },
        { l:'SOLICITADO POR', v:'Gonzalo Reyes M.' },
        { l:'TELÉFONO', v:'+569 9234 5678' },
        { l:'CENTRO COSTO', v:'Operaciones - Fase 4' },
        { l:'CORREO', v:'csoto@empresa.cl' },
        { l:'FECHA', v:'08/04/2026' },
        { l:'HORA', v:'06:00 hrs' },
        { l:'ORIGEN', v:'Terminal Norte' },
        { l:'DESTINO', v:'Faena Minera Sur' },
        { l:'VEHÍCULO', v:'DDEE-34 (H1 Premium)' },
        { l:'CONDUCTOR', v:'Francisco Reyes M.' },
      ].map((f, i) => (
        <div key={i} className="flex gap-2">
          <span className="text-gray-400 font-medium w-24 text-right flex-shrink-0">{f.l}</span>
          <span className="text-gray-800 font-semibold">{f.v}</span>
        </div>
      ))}
    </div>
    {/* Footer: tarifa + tiempo + estado */}
    <div className="grid grid-cols-3 border-t">
      <div className="text-center py-3" style={{ backgroundColor: `${DOSOFT_TEAL}10` }}>
        <div className="text-[9px] text-gray-400 mb-0.5">TARIFA</div>
        <div className="text-base font-black text-gray-800">$210.000</div>
      </div>
      <div className="text-center py-3 bg-gray-50 border-x border-gray-100">
        <div className="text-[9px] text-gray-400 mb-0.5">T. ESPERA</div>
        <div className="text-base font-bold text-gray-700">45 min</div>
      </div>
      <div className="text-center py-3 bg-green-50">
        <div className="text-[9px] text-gray-400 mb-0.5">ESTADO</div>
        <div className="text-base font-bold text-green-600">REALIZADO</div>
        <div className="text-[8px] text-green-500">&#10003; Confirmado</div>
      </div>
    </div>
  </div>
);

/* ======== Feature Section with Visual Mockup ======== */
const FeatureShowcase = ({ icon: Icon, title, description, details, reverse, color, mockup: Mockup }) => (
  <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-10 md:gap-16 items-center justify-center`}>
    <div className="flex-1 space-y-6 max-w-lg">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: `${color}15`, color }}>
        <Icon className="w-4 h-4" />
        {title}
      </div>
      <h3 className="text-3xl sm:text-4xl font-bold leading-tight" style={{ color: DOSOFT_GRAY }}>{description}</h3>
      <ul className="space-y-4">
        {details.map((d, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${color}15` }}>
              <CheckCircle className="w-4 h-4" style={{ color }} />
            </div>
            <span className="text-gray-600">{d}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex-1 w-full max-w-lg">
      <Mockup />
    </div>
  </div>
);

/* ======== Main Landing ======== */
export const LandingPage = () => {
  const navigate = useNavigate();
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registroForm, setRegistroForm] = useState({
    empresa_nombre: '',
    empresa_telefono: '',
    empresa_email: '',
    empresa_direccion: '',
    admin_nombre: '',
    admin_email: '',
    admin_password: '',
    admin_telefono: ''
  });

  const handleRegistroChange = (e) => {
    const { name, value } = e.target;
    setRegistroForm(prev => ({ ...prev, [name]: value }));
  };

  const handleRegistroSubmit = async (e) => {
    e.preventDefault();
    if (!registroForm.empresa_nombre || !registroForm.admin_nombre || 
        !registroForm.admin_email || !registroForm.admin_password) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }
    if (registroForm.admin_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/registro/empresa`, registroForm);
      if (response.data.success) {
        toast.success('¡Empresa registrada exitosamente!', {
          description: `Bienvenido ${response.data.admin.nombre}. Ya puede iniciar sesión.`
        });
        setShowRegistroModal(false);
        setRegistroForm({ empresa_nombre:'',empresa_telefono:'',empresa_email:'',empresa_direccion:'',admin_nombre:'',admin_email:'',admin_password:'',admin_telefono:'' });
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar la empresa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: Calendar, title: 'Calendario Operativo', description: 'Visualiza y gestiona todos los servicios programados en tiempo real con vistas por día, semana o mes.' },
    { icon: FileText, title: 'Vales Digitales', description: 'Genera, confirma y gestiona vales de servicio 100% digitales con folio autoincrementable.' },
    { icon: Users, title: 'Multi-Usuario', description: 'Administradores y clientes con accesos diferenciados según su rol en el sistema.' },
    { icon: Car, title: 'Gestión de Flota', description: 'Administra vehículos, conductores y asigna recursos a cada servicio fácilmente.' },
    { icon: BarChart3, title: 'Reportes y Exportación', description: 'Genera reportes de cuadratura con filtros, KPIs, exportación a Excel, PDF y Vales Digitales.' },
    { icon: Shield, title: 'Control de Estados', description: 'Seguimiento completo: Agendado, Confirmado, Realizado o Suspendido con tarifa y tiempo de espera.' },
    { icon: Download, title: 'Exportación de Vales', description: 'Exporta vales digitales detallados en PDF, con logo de empresa, un servicio por página.' },
    { icon: Layers, title: 'Multi-Empresa', description: 'Cada empresa opera con datos completamente aislados. Ideal para plataformas SaaS.' },
    { icon: Clock, title: 'Tiempo de Espera', description: 'Registra el tiempo de espera al finalizar un servicio e inclúyelo en reportes y facturación.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <img src="https://customer-assets.emergentagent.com/job_30a67381-0193-4972-adc8-4683263d2f57/artifacts/0sxu9ue3_Logo%20DoSoft.png" alt="DoSoft" className="h-10 w-auto" />
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" size="sm" onClick={() => setShowRegistroModal(true)} style={{ borderColor: DOSOFT_TEAL, color: DOSOFT_TEAL }} className="hover:bg-gray-50 text-xs sm:text-sm px-2 sm:px-4" data-testid="register-button">
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Registrar Empresa</span>
                <span className="sm:hidden">Registrar</span>
              </Button>
              <Button size="sm" onClick={() => navigate('/login')} style={{ backgroundColor: DOSOFT_TEAL }} className="hover:opacity-90 text-xs sm:text-sm px-3 sm:px-4" data-testid="login-button">
                Ingresar <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[calc(100vh-64px)] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: `${DOSOFT_TEAL}15`, color: DOSOFT_TEAL }}>
              <Smartphone className="w-4 h-4" /> Sistema 100% Digital
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ color: DOSOFT_GRAY }}>
              Gestión de Traslados
              <span className="block" style={{ color: DOSOFT_TEAL }}>Simple y Eficiente</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Digitaliza tu operación de transporte. Reemplaza los vales físicos por un sistema moderno que te permite gestionar servicios, clientes y reportes desde un solo lugar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setShowRegistroModal(true)} style={{ backgroundColor: DOSOFT_TEAL }} className="hover:opacity-90 text-lg px-8" data-testid="hero-register-button">
                Registrar mi Empresa <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })} className="text-lg px-8" style={{ borderColor: DOSOFT_TEAL, color: DOSOFT_TEAL }}>
                Ver Demo
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl" style={{ backgroundColor: `${DOSOFT_TEAL}10` }}></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${DOSOFT_TEAL}08` }}></div>
        {/* Scroll indicator */}
        <div className="flex justify-center pb-8 animate-bounce">
          <ChevronDown className="w-6 h-6 text-gray-400" />
        </div>
      </section>

      {/* Animated Demo Section */}
      <section id="demo" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${DOSOFT_TEAL}15`, color: DOSOFT_TEAL }}>
              <Eye className="w-4 h-4" /> Demo Interactiva
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: DOSOFT_GRAY }}>
              Así Funciona el Sistema
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Desde la solicitud del servicio hasta la exportación del reporte, todo en una sola plataforma.
            </p>
          </div>
          <BookingScreencast />
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="py-20" style={{ backgroundColor: DOSOFT_GRAY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">¿Cansado del Papeleo?</h2>
              <p className="text-gray-300 text-lg mb-6">Los vales físicos generan múltiples problemas en las empresas de transporte:</p>
              <ul className="space-y-4">
                {['Vales extraviados o ilegibles','Demoras en la facturación mensual','Errores de transcripción de datos','Dificultad para controlar servicios','Sin visibilidad en tiempo real'].map((p, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0"><span className="text-red-400 text-sm">&#10005;</span></div>
                    <span className="text-gray-300">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6" style={{ color: DOSOFT_TEAL }}>La Solución</h3>
              <p className="text-gray-300 mb-6">Velify digitaliza todo el flujo de trabajo, desde la solicitud hasta el reporte de facturación.</p>
              <ul className="space-y-4">
                {['Reduce errores de transcripción manual','Elimina pérdida de vales físicos','Acelera el proceso de facturación','Mejora la comunicación con clientes','Centraliza toda la información operativa','Acceso desde cualquier dispositivo'].map((b, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: DOSOFT_TEAL }} />
                    <span className="text-gray-300">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: DOSOFT_GRAY }}>Todas las Funcionalidades</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Una plataforma completa para gestionar tu operación de transporte de principio a fin.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${DOSOFT_TEAL}15` }}>
                    <f.icon className="w-6 h-6" style={{ color: DOSOFT_TEAL }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: DOSOFT_GRAY }}>{f.title}</h3>
                  <p className="text-gray-600 text-sm">{f.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase: Calendario Operativo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <FeatureShowcase
            icon={Calendar}
            title="Calendario Operativo"
            description="Visualiza toda tu operación en un solo lugar"
            color={DOSOFT_TEAL}
            mockup={CalendarioMockup}
            details={[
              'Vista mensual, semanal y diaria con código de colores por estado',
              'Servicios codificados: Agendado, Confirmado, Realizado, Suspendido',
              'Click en cualquier servicio para ver detalle y cambiar estado',
              'Asignación rápida de vehículo y conductor',
            ]}
          />
        </div>
      </section>

      {/* Showcase: Reportes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <FeatureShowcase
            icon={BarChart3}
            title="Reportes y Facturación"
            description="Genera reportes detallados para tu cuadratura mensual"
            color="#6366F1"
            reverse
            mockup={ReportesMockup}
            details={[
              'Filtros por rango de fechas, cliente, estado y búsqueda por folio',
              'KPIs en tiempo real: total servicios, realizados, monto facturable',
              'Exportación a Excel con todas las columnas y tiempo de espera',
              'Exportación a PDF profesional y Vales Digitales con logo',
            ]}
          />
        </div>
      </section>

      {/* Showcase: Flota */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <FeatureShowcase
            icon={Truck}
            title="Gestión de Flota"
            description="Administra tus vehículos y conductores eficientemente"
            color="#F59E0B"
            mockup={FlotaMockup}
            details={[
              'Registro completo: patente, marca, modelo, año y capacidad',
              'Gestión de conductores con teléfono, RUT y licencia',
              'Estado de disponibilidad de cada vehículo',
              'Información visible en cada vale digital exportado',
            ]}
          />
        </div>
      </section>

      {/* Showcase: Clientes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <FeatureShowcase
            icon={UserCheck}
            title="Gestión de Clientes y Usuarios"
            description="Control total de accesos y permisos por rol"
            color="#10B981"
            reverse
            mockup={ClientesMockup}
            details={[
              'Roles diferenciados: Administrador y Cliente',
              'Cada cliente asociado a su empresa con centros de costo',
              'Los clientes solo ven sus propios servicios y solicitudes',
              'Formato de RUT chileno y validación de teléfono automática',
            ]}
          />
        </div>
      </section>

      {/* Showcase: Vales PDF */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <FeatureShowcase
            icon={FileText}
            title="Vales Digitales PDF"
            description="Documentos profesionales con tu identidad corporativa"
            color="#EC4899"
            mockup={ValePDFMockup}
            details={[
              'PDF con logo de la empresa configurado en el sistema',
              'Un vale por página con todos los detalles del servicio',
              'Incluye tarifa, tiempo de espera, estado y confirmación',
              'Folio autoincrementable para trazabilidad completa',
            ]}
          />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16" style={{ backgroundColor: DOSOFT_TEAL }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { num: '100%', label: 'Digital', sub: 'Sin papel' },
              { num: '24/7', label: 'Disponible', sub: 'Acceso web' },
              { num: '3', label: 'Formatos', sub: 'Excel, PDF, Vales' },
              { num: '∞', label: 'Empresas', sub: 'Multi-tenant' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl sm:text-4xl font-bold mb-1">{s.num}</div>
                <div className="text-white/90 font-medium">{s.label}</div>
                <div className="text-white/60 text-sm">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: DOSOFT_GRAY }}>¿Cómo Funciona?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Un flujo simple y eficiente para gestionar tus servicios de transporte.</p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { step: '1', title: 'Solicitud', desc: 'El cliente solicita un servicio con fecha, hora, pasajero y ruta.', icon: ClipboardCheck },
              { step: '2', title: 'Asignación', desc: 'Se asigna vehículo y conductor al servicio programado.', icon: Car },
              { step: '3', title: 'Confirmación', desc: 'El administrador confirma el servicio y define la tarifa.', icon: CheckCircle },
              { step: '4', title: 'Realización', desc: 'Se marca como realizado con tarifa final y tiempo de espera.', icon: CircleDot },
              { step: '5', title: 'Reporte', desc: 'Se genera el reporte de cuadratura y vales digitales para facturación.', icon: FileSpreadsheet },
            ].map((item, i) => (
              <div key={i} className="text-center relative">
                <div className="w-14 h-14 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ backgroundColor: DOSOFT_TEAL }}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-semibold mb-1" style={{ color: DOSOFT_GRAY }}>{item.step}. {item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
                {i < 4 && <ArrowRight className="hidden md:block absolute top-7 -right-4 w-5 h-5 text-gray-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Comienza a Digitalizar tu Operación</h2>
          <p className="text-lg text-gray-400 mb-8">Únete a las empresas de transporte que ya optimizaron su gestión con nuestro sistema.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setShowRegistroModal(true)} className="text-lg px-8" style={{ backgroundColor: DOSOFT_TEAL }} data-testid="cta-register-button">
              <Building2 className="w-5 h-5 mr-2" /> Registrar mi Empresa
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="text-lg px-8 border-gray-600 text-gray-300 hover:bg-gray-800">
              Ya tengo cuenta <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: DOSOFT_GRAY }} className="text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <img src="https://customer-assets.emergentagent.com/job_30a67381-0193-4972-adc8-4683263d2f57/artifacts/0sxu9ue3_Logo%20DoSoft.png" alt="DoSoft" className="h-10 w-auto brightness-0 invert mb-4 md:mb-0" />
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} DoSoft. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      {showRegistroModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: DOSOFT_GRAY }}>Registrar Nueva Empresa</h2>
                <p className="text-gray-500 text-sm mt-1">Complete los datos para crear su cuenta</p>
              </div>
              <button onClick={() => setShowRegistroModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="close-modal-button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleRegistroSubmit} className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold" style={{ color: DOSOFT_TEAL }}>
                  <Building2 className="w-5 h-5" /> Datos de la Empresa
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="empresa_nombre">Nombre de la Empresa *</Label>
                    <div className="relative mt-1"><Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="empresa_nombre" name="empresa_nombre" value={registroForm.empresa_nombre} onChange={handleRegistroChange} placeholder="Ej: Transportes Rápidos SpA" className="pl-10" required data-testid="empresa-nombre-input" /></div>
                  </div>
                  <div>
                    <Label htmlFor="empresa_telefono">Teléfono</Label>
                    <div className="relative mt-1"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="empresa_telefono" name="empresa_telefono" value={registroForm.empresa_telefono} onChange={handleRegistroChange} placeholder="+56 9 1234 5678" className="pl-10" data-testid="empresa-telefono-input" /></div>
                  </div>
                  <div>
                    <Label htmlFor="empresa_email">Email de Contacto</Label>
                    <div className="relative mt-1"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="empresa_email" name="empresa_email" type="email" value={registroForm.empresa_email} onChange={handleRegistroChange} placeholder="contacto@empresa.cl" className="pl-10" data-testid="empresa-email-input" /></div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="empresa_direccion">Dirección</Label>
                    <div className="relative mt-1"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="empresa_direccion" name="empresa_direccion" value={registroForm.empresa_direccion} onChange={handleRegistroChange} placeholder="Av. Principal 123, Santiago" className="pl-10" data-testid="empresa-direccion-input" /></div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200"></div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold" style={{ color: DOSOFT_TEAL }}>
                  <User className="w-5 h-5" /> Datos del Administrador
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="admin_nombre">Nombre Completo *</Label>
                    <div className="relative mt-1"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="admin_nombre" name="admin_nombre" value={registroForm.admin_nombre} onChange={handleRegistroChange} placeholder="Juan Pérez González" className="pl-10" required data-testid="admin-nombre-input" /></div>
                  </div>
                  <div>
                    <Label htmlFor="admin_email">Email de Acceso *</Label>
                    <div className="relative mt-1"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="admin_email" name="admin_email" type="email" value={registroForm.admin_email} onChange={handleRegistroChange} placeholder="admin@empresa.cl" className="pl-10" required data-testid="admin-email-input" /></div>
                  </div>
                  <div>
                    <Label htmlFor="admin_telefono">Teléfono</Label>
                    <div className="relative mt-1"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="admin_telefono" name="admin_telefono" value={registroForm.admin_telefono} onChange={handleRegistroChange} placeholder="+56 9 1234 5678" className="pl-10" data-testid="admin-telefono-input" /></div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="admin_password">Contraseña *</Label>
                    <div className="relative mt-1"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input id="admin_password" name="admin_password" type="password" value={registroForm.admin_password} onChange={handleRegistroChange} placeholder="Mínimo 6 caracteres" className="pl-10" required minLength={6} data-testid="admin-password-input" /></div>
                    <p className="text-xs text-gray-500 mt-1">La contraseña debe tener al menos 6 caracteres</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowRegistroModal(false)} className="flex-1" disabled={isSubmitting}>Cancelar</Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: DOSOFT_TEAL }} disabled={isSubmitting} data-testid="submit-registro-button">
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrando...</>) : (<><CheckCircle className="w-4 h-4 mr-2" />Registrar Empresa</>)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp */}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white px-4 py-3 rounded-full shadow-lg transition-all hover:scale-105" data-testid="whatsapp-button">
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="hidden sm:inline font-medium">¿Tienes dudas? Escríbenos</span>
      </a>
    </div>
  );
};
